import { ImagePaths } from '../../lib/imageUrls.js';

export const sharepointSiteOwners = {
    id: 24,
    slug: 'sharepoint-site-owners',
    ogImage: '/og/courses/sharepoint-site-owners.jpg',
    courseCode: 'SHAREPOINT-OWNER-001',
    title: 'SharePoint Site Owners — Governance and Configuration',
    subtitle: 'Manage, configure, and govern SharePoint sites with confidence',
    valueProposition: 'Build the skills to own and operate SharePoint sites effectively — configuring permissions, organising content, managing libraries, and applying governance practices that keep your site useful and secure.',
    duration: '8 hours',
    students: 'New',
    level: 'Intermediate',
    organization: 'EDUC4TE',
    image: ImagePaths.COURSE_3,
    shortDescription: 'SHAREPOINT-OWNER-001 — SharePoint site ownership covering permissions, libraries, content management, and governance for site owners.',
    price: {
        aud: 499,
        currency: 'AUD',
        earlyBird: 399
    },
    stripeConfig: {
    priceId: 'price_1TPMQhKu1nyc322UHWj5Pg4a',
    productId: 'prod_UO8iw02ZwKxGbc'
  },

    snapshot: {
        whoItsFor: [
            'SharePoint Site Owners',
            'Team and Project Site Owners',
            'Intranet Managers and Editors',
            'IT Administrators managing SharePoint'
        ],
        accreditation: 'Aligned to Microsoft 365 adoption guidance and Australian Privacy Act 1988'
    },

    logistics: {
        classSize: 'Maximum 20 participants for hands-on practice',
        requirements: [
            'Active Microsoft 365 licence with SharePoint Online access',
            'Site Owner permissions on at least one SharePoint site',
            'Basic familiarity with SharePoint as a user (browsing sites, opening documents)',
            'Computer with internet access and a modern browser'
        ]
    },

    overview: 'SharePoint Site Owners carry more responsibility than most realise — they control who can access content, how information is organised, and whether their site supports or hinders team productivity. This practical 8-hour course gives site owners the skills to manage their SharePoint environment with confidence. You will configure site settings and navigation, build effective document libraries and lists, apply permission models that protect sensitive content, manage site membership and access requests, and establish lightweight governance practices that keep your site organised over time. The course covers both team sites and communication sites, with practical exercises using a live Microsoft 365 tenant throughout. Australian participants will also learn how site ownership decisions intersect with Privacy Act 1988 obligations, particularly around personal information stored in SharePoint libraries. By the end, you will have a fully configured site, a clear permission strategy, and a maintenance routine you can follow independently.',

    outcomes: [
        'Configure SharePoint site settings, navigation, and branding for a team or communication site',
        'Build and manage document libraries and lists with appropriate column structures and views',
        'Apply SharePoint permission levels and inheritance to control access at site, library, and item level',
        'Manage site membership, access requests, and external sharing in line with organisational policy',
        'Create and maintain site content types for consistent document management across libraries',
        'Identify and remediate common site issues including broken permissions, orphaned content, and storage overuse',
        'Apply Privacy Act 1988 principles to decisions about personal information stored in SharePoint',
        'Establish a governance routine covering site reviews, membership audits, and content housekeeping'
    ],

    audience: [
        'SharePoint Site Owners taking formal ownership of a site',
        'Project and team managers who run SharePoint-based collaboration spaces',
        'Intranet managers and internal communications teams',
        'IT administrators supporting SharePoint site owners across their organisation',
        'Executive assistants and operations staff managing departmental SharePoint sites',
        'Anyone transitioning from SharePoint on-premises to SharePoint Online'
    ],

    prerequisites: [
        'Basic experience using SharePoint as a member or visitor (navigating, opening files)',
        'Active Microsoft 365 licence with SharePoint Online access',
        'Site Owner or higher permissions on a SharePoint site for lab exercises',
        'No SharePoint administration experience required'
    ],

    modules: [
        {
            title: 'SharePoint Architecture and Site Owner Responsibilities',
            duration: '1 hour',
            topics: [
                'SharePoint Online architecture: tenants, site collections, sites, and subsites',
                'Difference between team sites, communication sites, and hub sites',
                'Site Owner vs Site Member vs Site Visitor: what each can do',
                'Site Owner accountability: permissions, content, and compliance obligations',
                'Overview of the SharePoint admin centre from an owner perspective'
            ]
        },
        {
            title: 'Site Configuration and Navigation',
            duration: '1.5 hours',
            topics: [
                'Configuring site settings: title, description, logo, and regional settings',
                'Designing site navigation: top nav, quick launch, and footer links',
                'Adding and configuring site pages and web parts',
                'Connecting a site to a hub site for unified navigation and branding',
                'Managing site features and enabling/disabling capabilities',
                'Lab: Configure a site from scratch with navigation and branding (20 min)'
            ]
        },
        {
            title: 'Document Libraries and Lists',
            duration: '2 hours',
            topics: [
                'Creating and configuring document libraries with custom columns and metadata',
                'Library views: creating, filtering, and setting defaults for different audiences',
                'Versioning settings: major/minor versions, approval workflows, and history',
                'Configuring SharePoint Lists for structured data and simple tracking',
                'Content types: creating, applying, and managing across a site',
                'Lab: Build a document library with metadata, views, and versioning (25 min)'
            ]
        },
        {
            title: 'Permissions and Access Management',
            duration: '2 hours',
            topics: [
                'SharePoint permission levels: Full Control, Edit, Contribute, Read, and custom',
                'Managing site membership: adding members, owners, and visitors',
                'Breaking permission inheritance at library and item level',
                'Handling access requests: approval workflow and audit trail',
                'External sharing settings: configuring guest access safely',
                'Privacy Act 1988 considerations for personal information in SharePoint',
                'Lab: Configure permissions for a multi-team site with sensitive content (30 min)'
            ]
        },
        {
            title: 'Site Governance and Ongoing Maintenance',
            duration: '1.5 hours',
            topics: [
                'Site usage analytics: understanding traffic, storage, and activity reports',
                'Membership and permissions auditing as a routine site owner task',
                'Stale content identification: recycle bin, version cleanup, and archival',
                'Applying retention labels to libraries for compliance obligations',
                'Creating a site governance checklist for monthly and quarterly reviews',
                'Lab: Complete a site health review and create a governance checklist (20 min)'
            ]
        }
    ],

    format: {
        type: 'Live instructor-led day workshop, intensive online delivery, or lunch-and-learn (8 x 1-hour sessions over 8 days)',
        includes: [
            '8 hours of instructor-led training with a live SharePoint tenant',
            '5 hands-on labs with step-by-step exercise guides',
            'Site Owner Quick Reference Card (PDF)',
            'Permission model decision guide',
            'Site governance checklist template',
            'Certificate of completion',
            'Access to course materials for 12 months'
        ]
    },

    nextIntake: {
        date: 'Register your interest — we will confirm the next cohort',
        details: 'Full-day instructor-led workshop (9:00 AM to 5:00 PM) or two half-day online sessions'
    },

    faqs: [
        {
            question: 'Do I need to be technically minded to take this course?',
            answer: 'No. This course is designed for site owners who manage SharePoint in a business context, not IT professionals. If you can navigate SharePoint and open documents, you have enough background. We explain all concepts in plain language with practical exercises you apply immediately.'
        },
        {
            question: 'Will I need my own SharePoint site for the labs?',
            answer: 'Yes, you need access to a SharePoint site where you hold Site Owner permissions. This can be an existing site in your organisation (a test or project site works well) or a Microsoft 365 trial tenant. We advise against using a production homepage or high-traffic site for the labs.'
        },
        {
            question: 'What is the difference between this course and the SharePoint Copilot in a Day course?',
            answer: 'This course teaches core site ownership skills — configuration, permissions, libraries, and governance. The SharePoint Copilot in a Day course is a specialist follow-on that prepares an already-functioning SharePoint environment for Microsoft 365 Copilot activation. We recommend taking this course first if you are new to site ownership.'
        },
        {
            question: 'Is this course relevant for SharePoint on-premises environments?',
            answer: 'The course focuses on SharePoint Online (Microsoft 365). While the concepts around permissions and libraries are broadly similar, the interface, admin options, and governance tools differ significantly. If your organisation is migrating from on-premises to SharePoint Online, this course is ideal preparation.'
        },
        {
            question: 'Can we run this course in-house for our whole site owner community?',
            answer: 'Yes. In-house delivery is particularly effective when you want all site owners in your organisation working from a consistent governance baseline. We can tailor the labs to use your actual SharePoint environment and align to your internal policies. Contact us for a custom quote.'
        },
        {
            question: 'Does the course cover Microsoft Teams-connected sites?',
            answer: 'Yes. When a Microsoft Teams team is created, a SharePoint team site is automatically provisioned behind it. The course covers how Teams-connected site ownership works, what site owners can and cannot change in that context, and how to manage content and permissions across both surfaces.'
        },
        {
            question: 'Will I receive a certificate after completing the course?',
            answer: 'Yes. Participants receive an Educ4te Certificate of Completion upon finishing the course and completing the lab exercises. The certificate can be shared on LinkedIn and added to your professional development record.'
        },
        {
            question: 'Can multiple people from our team enrol at the same group rate?',
            answer: 'Yes. For teams of 5 or more, contact us for a private quote. Training your site owner community together ensures everyone applies consistent governance standards across your SharePoint environment.'
        },
      {
        question: 'Can this course be delivered as lunch-and-learn sessions?',
        answer: 'Yes. The course runs as eight one-hour sessions over eight days, with the same content and outcomes as the one-day format, paced around the working day so people do not need to be away from their desks for a full day. Delivery is live online or on-site, and private cohorts are available for teams. Hands-on labs are spread across the sessions, so participants need access to the lab environment throughout. Contact us to arrange a schedule.'
      }
    ]
};
